import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Switch,
    Typography,
    Grid,
    Card,
    Button,
    Chip,
    Avatar,
    LinearProgress,
    TextField
} from '@mui/material';
import {
    GoogleMap,
    InfoWindow,
    MarkerF
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import RawChart from 'react-apexcharts';
import { api } from 'common';
import moment from 'moment/min/moment-with-locales';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SensorsIcon from '@mui/icons-material/Sensors';

import carIconOnline from '../assets/img/car.png';
import carIconOffline from '../assets/img/car_gray.png';

const resolveComponent = (comp) => {
    let resolved = comp;
    while (resolved && typeof resolved === 'object' && resolved.default) {
        resolved = resolved.default;
    }
    return typeof resolved === 'function' ? resolved : null;
};

const Chart = resolveComponent(RawChart);

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isPt = !i18n.language || i18n.language.toLowerCase().startsWith('pt');
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [showAllPopups, setShowAllPopups] = useState(true);
    const [infoWindowShown, setInfoWindowShown] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [todayRoutesCount, setTodayRoutesCount] = useState(0);
    const mapRef = useRef(null);
    const dispatch = useDispatch();

    const onMapLoad = useCallback((mapInstance) => {
        mapRef.current = mapInstance;
    }, []);

    const focusVehicle = (item) => {
        setSelectedMarkerId(item.id);
        if (mapRef.current && item.lat && item.lng) {
            mapRef.current.panTo({ lat: item.lat, lng: item.lng });
            mapRef.current.setZoom(15);
        }
        setInfoWindowShown(prev => ({
            ...prev,
            [item.id]: true
        }));
        setLocations(prev => prev.map(l => l.id === item.id ? { ...l, show: true } : l));
    };

    const recenterMap = () => {
        if (mapRef.current && mylocation) {
            mapRef.current.panTo(mylocation);
            mapRef.current.setZoom(12);
        }
    };

    const {fetchDrivers, clearFetchDrivers} = api;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (!usersdata) return;
        let allDrivers = [];
        if (usersdata.drivers && usersdata.drivers.length > 0) {
            allDrivers = [...usersdata.drivers];
        }
        if (usersdata.users && usersdata.users.length > 0) {
            const usersDrivers = usersdata.users.filter(u => u.usertype === 'driver');
            for (let d of usersDrivers) {
                if (!allDrivers.some(existing => existing.id === d.id)) {
                    allDrivers.push(d);
                }
            }
        }

        if (allDrivers.length > 0) {
            const liveBookings = bookinglistdata.bookings ? bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED') : [];
            const carsList = cars || [];
            let locs = [];
            for (let i = 0; i < allDrivers.length; i++) {
                const userProfile = (usersdata.users && usersdata.users.find(u => u.id === allDrivers[i].id || (allDrivers[i].uid && u.id === allDrivers[i].uid))) || allDrivers[i];
                const loc = allDrivers[i].location || userProfile?.location;

                if (loc && loc.lat && loc.lng) {
                    let carTypeName = '';
                    let bookingRef = null;
                    let carTypePos = 0;
                    for (let j = 0; j < carsList.length; j++) {
                        if (carsList[j].name === allDrivers[i].carType) {
                            carTypeName = carsList[j].name;
                            carTypePos = carsList[j].pos;
                        }
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === allDrivers[i].uid || liveBookings[j].driver === allDrivers[i].id) {
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    
                    if (showActiveOnly && !allDrivers[i].driverActiveStatus) continue;

                    const vMake = allDrivers[i].vehicleMake || userProfile?.vehicleMake || '';
                    const vModel = allDrivers[i].vehicleModel || userProfile?.vehicleModel || '';
                    const vName = [vMake, vModel].filter(Boolean).join(' ');
                    const vehicleDisplay = vName 
                        ? (carTypeName && carTypeName !== 'N/A' ? `${vName} (${carTypeName})` : vName)
                        : (carTypeName && carTypeName !== 'N/A' ? carTypeName : t('no_car_assign_text', isPt ? 'Nenhum veículo atribuído' : 'No vehicle assign'));
                    const dId = allDrivers[i].id || allDrivers[i].uid;
                    let latestBookingTime = null;
                    if (bookinglistdata.bookings && bookinglistdata.bookings.length > 0) {
                        for (let b of bookinglistdata.bookings) {
                            if (b.driver === dId || (b.requestedDrivers && b.requestedDrivers[dId])) {
                                const bTime = b.trip_end_time || b.trip_start_time || b.tripdate || b.bookingDate || b.date;
                                if (bTime) {
                                    const parsed = typeof bTime === 'number' ? bTime : new Date(bTime).getTime();
                                    if (!isNaN(parsed) && parsed > 0 && (!latestBookingTime || parsed > latestBookingTime)) {
                                        latestBookingTime = parsed;
                                    }
                                }
                            }
                        }
                    }

                    const locTimestamp = loc && (loc.updatedAt || loc.timestamp || loc.time || loc.date);
                    const lastSeen = locTimestamp
                        || allDrivers[i].lastTimeSeen 
                        || userProfile?.lastTimeSeen
                        || latestBookingTime
                        || (allDrivers[i].driverActiveStatus ? Date.now() : null)
                        || allDrivers[i].updatedAt 
                        || userProfile?.updatedAt 
                        || userProfile?.updateAt
                        || allDrivers[i].createdAt
                        || userProfile?.createdAt
                        || null;

                    const firstName = allDrivers[i].firstName || userProfile?.firstName || '';
                    const lastName = allDrivers[i].lastName || userProfile?.lastName || '';
                    const drivername = `${firstName} ${lastName}`.trim() || t('driver_name', isPt ? 'Motorista' : 'Driver');
                    const markerId = allDrivers[i].id || allDrivers[i].uid || i;

                    const plate = allDrivers[i].vehicleNumber || userProfile?.vehicleNumber || '';

                    locs.push({
                        id: markerId,
                        lat: loc.lat,
                        lng: loc.lng,
                        drivername: drivername,
                        vehicleDisplay: vehicleDisplay,
                        plate: plate,
                        vehicleMake: vMake,
                        vehicleModel: vModel,
                        locationTimestamp: locTimestamp,
                        lastTimeSeen: lastSeen,
                        cartype: allDrivers[i].carType,
                        bookingRef: bookingRef,
                        driverActiveStatus: !!allDrivers[i].driverActiveStatus,
                        show: infoWindowShown[markerId] !== undefined ? infoWindowShown[markerId] : showAllPopups,
                        carTypeName: carTypeName ? carTypeName : 'N/A',
                        carTypePos: carTypePos ? carTypePos : 0
                    });
                }
            }
            setLocations(locs);
        } else {
            setLocations([]);
        }
    }, [usersdata, auth?.profile?.usertype, auth?.profile?.uid, cars, bookinglistdata.bookings, settings, showActiveOnly, showAllPopups, infoWindowShown, t, isPt]);

    useEffect(() => {
        if (!mylocation && locations && locations.length > 0) {
            setMylocation({
                lat: locations[0].lat,
                lng: locations[0].lng
            });
        }
    }, [locations, mylocation]);

    useEffect(() => {
        if (bookinglistdata.bookings && auth && auth.profile) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            const usertype = auth.profile.usertype;
            const uid = auth.profile.uid;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === uid && usertype === 'fleetadmin')|| usertype === 'admin') ) {
                    const { tripdate, convenience_fees, driver_share, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (driver_share && parseFloat(driver_share) > 0 && usertype === 'fleetadmin'){
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(driver_share);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(driver_share);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(driver_share);
                    }
                }
            }
            let todayRoutes = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                const b = bookinglistdata.bookings[i];
                const bDateStr = b.tripdate || b.bookingDate || b.date;
                if (bDateStr) {
                    const bDate = new Date(bDateStr);
                    if (bDate.getDate() === today.getDate() && bDate.getMonth() === today.getMonth() && bDate.getFullYear() === today.getFullYear()) {
                        todayRoutes++;
                    }
                }
            }
            setTodayRoutesCount(todayRoutes);
        }
    }, [bookinglistdata.bookings, settings, auth]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: ["#109496", "#f28e8e"],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            chart: {
                height: 350,
                type: 'area'
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
        },
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];
    useEffect(() => {
        if (usersdata && usersdata.users && auth && auth.profile) {
            let driverCount = 0;
            let activeCount = 0;
            const usertype = auth.profile.usertype;
            const uid = auth.profile.uid;
            const tId = auth.profile.tenant_id || auth.profile.tenantId;
            const isSuper = auth.profile.is_superadmin || usertype === 'superadmin';

            for (let i = 0; i < usersdata.users.length; i++) {
                const u = usersdata.users[i];
                const matchesTenant = isSuper || (tId && (u.tenant_id === tId || u.tenantId === tId)) || (usertype === 'fleetadmin' && u.fleetadmin === uid) || (!tId && usertype === 'admin');
                if (u.usertype === 'driver' && matchesTenant) {
                    driverCount++;
                    if (u.driverActiveStatus === true) {
                        activeCount++;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
        }
    }, [usersdata, auth]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    const toggleShowActiveOnly = () => {
        setShowActiveOnly(!showActiveOnly);
    };

    const toggleShowAllPopups = () => {
        const nextState = !showAllPopups;
        setShowAllPopups(nextState);
        const newShown = {};
        setLocations((prev) => prev.map((l) => {
            newShown[l.id] = nextState;
            return { ...l, show: nextState };
        }));
        setInfoWindowShown(newShown);
    };

    const formatLastSeen = (timestamp, isActive) => {
        if (!timestamp) {
            return isActive ? (isPt ? 'Ativo agora' : 'Active now') : (isPt ? 'Sem registro recente' : 'No recent record');
        }
        try {
            const num = typeof timestamp === 'number' ? timestamp : Number(timestamp);
            const m = !isNaN(num) && num > 0 ? moment(num) : moment(timestamp);
            if (!m.isValid()) {
                return isActive ? (isPt ? 'Ativo agora' : 'Active now') : (isPt ? 'Sem registro recente' : 'No recent record');
            }
            return `${m.format('lll')} (${m.fromNow()})`;
        } catch (e) {
            return isActive ? (isPt ? 'Ativo agora' : 'Active now') : (isPt ? 'Sem registro recente' : 'No recent record');
        }
    };

    const handleMarkerClick = (id) => {
        let newLocations = locations.map((location) => {
            if (location.id === id) {
                const nextShow = !location.show;
                setInfoWindowShown((prev) => ({
                    ...prev,
                    [id]: nextShow
                }));
                return { ...location, show: nextShow };
            }
            return location;
        });
        setLocations(newLocations);
    };

    const handleClose = (id) => {
        let newLocations = locations.map((location) => {
            if (location.id === id) {
                setInfoWindowShown((prev) => ({
                    ...prev,
                    [id]: false
                }));
                return { ...location, show: false };
            }
            return location;
        });
        setLocations(newLocations);
    };
    const filteredLocations = locations.filter(l => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            (l.drivername && l.drivername.toLowerCase().includes(q)) ||
            (l.plate && l.plate.toLowerCase().includes(q)) ||
            (l.vehicleDisplay && l.vehicleDisplay.toLowerCase().includes(q))
        );
    });

    const fleetOccupancy = driverCount > 0 ? Math.min(100, Math.round((activeCount / driverCount) * 100)) : 0;
    const totalVehiclesCount = cars && cars.length > 0 ? cars.length : driverCount;

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: '100%', boxSizing: 'border-box' }}>
                {/* 1. Header do Dashboard */}
                <Box sx={{
                    mb: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
                            {t('fleet_command_center', isPt ? 'Torre de Controle & Gestão de Frota' : 'Fleet Command Center')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
                            {t('fleet_command_subtitle', isPt ? 'Monitoramento em tempo real de veículos, ordens de serviço e condutores em campo' : 'Real-time monitoring of vehicles, routes and active drivers')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<SensorsIcon sx={{ fontSize: '15px !important', color: '#16a34a !important' }} />}
                            label={isPt ? "Telemetria Ativa" : "Live Telemetry"}
                            sx={{
                                bgcolor: '#dcfce7',
                                color: '#15803d',
                                fontWeight: 700,
                                fontSize: 12,
                                border: '1px solid #bbf7d0',
                                height: 32
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={recenterMap}
                            startIcon={<MyLocationIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#e2e8f0',
                                color: '#334155',
                                bgcolor: '#ffffff',
                                fontWeight: 600,
                                borderRadius: '10px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                height: 32,
                                '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                            }}
                        >
                            {isPt ? "Recentralizar Mapa" : "Recenter Map"}
                        </Button>
                    </Box>
                </Box>

                {/* 2. Top KPI Cards */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {/* Card 1: Veículos Ativos / Em Rota */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2.5,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 6px 12px -2px rgba(0,0,0,0.02)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px -5px rgba(0,0,0,0.06)' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                                    {isPt ? "Veículos em Operação" : "Active Vehicles"}
                                </Typography>
                                <Avatar sx={{ bgcolor: '#dcfce7', color: '#16a34a', width: 42, height: 42, borderRadius: '12px' }}>
                                    <DirectionsCarIcon sx={{ fontSize: 22 }} />
                                </Avatar>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    {activeCount}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, bgcolor: '#f0fdf4', px: 1, py: 0.2, borderRadius: '6px' }}>
                                    {isPt ? "Online agora" : "Online now"}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                {fleetOccupancy}% {isPt ? "da frota em deslocamento" : "of fleet active"}
                            </Typography>
                        </Card>
                    </Grid>

                    {/* Card 2: Motoristas na Frota */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2.5,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 6px 12px -2px rgba(0,0,0,0.02)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px -5px rgba(0,0,0,0.06)' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                                    {isPt ? "Motoristas Habilitados" : "Fleet Drivers"}
                                </Typography>
                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 42, height: 42, borderRadius: '12px' }}>
                                    <PersonIcon sx={{ fontSize: 22 }} />
                                </Avatar>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    {driverCount}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, bgcolor: '#f0fdf4', px: 1, py: 0.2, borderRadius: '6px' }}>
                                    {driverCount - activeCount} {isPt ? "disponíveis" : "idle"}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                {isPt ? "Condutores cadastrados" : "Registered drivers"}
                            </Typography>
                        </Card>
                    </Grid>

                    {/* Card 3: Total de Veículos Cadastrados */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2.5,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 6px 12px -2px rgba(0,0,0,0.02)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px -5px rgba(0,0,0,0.06)' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                                    {isPt ? "Frota de Veículos" : "Total Fleet"}
                                </Typography>
                                <Avatar sx={{ bgcolor: '#eef2ff', color: '#4f46e5', width: 42, height: 42, borderRadius: '12px' }}>
                                    <EmojiTransportationIcon sx={{ fontSize: 22 }} />
                                </Avatar>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    {totalVehiclesCount}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#4f46e5', fontWeight: 700, bgcolor: '#eef2ff', px: 1, py: 0.2, borderRadius: '6px' }}>
                                    {isPt ? "Ativos corporativos" : "Assets"}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                {isPt ? "Veículos mapeados na base" : "Mapped vehicles in fleet"}
                            </Typography>
                        </Card>
                    </Grid>

                    {/* Card 4: Rotas do Dia */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2.5,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 6px 12px -2px rgba(0,0,0,0.02)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px -5px rgba(0,0,0,0.06)' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                                    {isPt ? "Rotas de Hoje" : "Today's Routes"}
                                </Typography>
                                <Avatar sx={{ bgcolor: '#fffbeb', color: '#d97706', width: 42, height: 42, borderRadius: '12px' }}>
                                    <AssessmentIcon sx={{ fontSize: 22 }} />
                                </Avatar>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    {todayRoutesCount}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, bgcolor: '#fffbeb', px: 1, py: 0.2, borderRadius: '6px' }}>
                                    {isPt ? "Ordens de Serviço" : "Work orders"}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                {allCompleteCount.reduce((a, b) => a + b, 0)} {isPt ? "concluídas no histórico" : "completed overall"}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* 3. Seção do Mapa + Sidebar de Frota */}
                <Grid container spacing={2.5}>
                    {/* Mapa Principal */}
                    <Grid item xs={12} lg={8.5}>
                        <Card sx={{
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
                            bgcolor: '#ffffff'
                        }}>
                            {/* Toolbar superior do mapa */}
                            <Box sx={{
                                px: 2.5,
                                py: 1.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                borderBottom: '1px solid #f1f5f9'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        {isPt ? "Mapa da Frota em Tempo Real" : "Live Fleet Map"}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`${locations.filter(l => l.driverActiveStatus).length} ${isPt ? "online" : "online"}`}
                                        sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 11, height: 22 }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                            {isPt ? "Apenas Ativos" : "Only Active"}
                                        </Typography>
                                        <Switch
                                            checked={showActiveOnly ?? false}
                                            color="success"
                                            size="small"
                                            onChange={toggleShowActiveOnly}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                                            {isPt ? "Exibir Popups" : "Show Popups"}
                                        </Typography>
                                        <Switch
                                            checked={showAllPopups}
                                            color="primary"
                                            size="small"
                                            onChange={toggleShowAllPopups}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Google Map */}
                            {mylocation && mylocation.lat ? (
                                <GoogleMap
                                    zoom={12}
                                    center={mylocation}
                                    onLoad={onMapLoad}
                                    mapContainerStyle={{ height: '580px', width: '100%' }}
                                    options={{
                                        disableDefaultUI: false,
                                        zoomControl: true,
                                        mapTypeControl: false,
                                        streetViewControl: false,
                                        fullscreenControl: true
                                    }}
                                >
                                    {locations && locations.map((marker) => (
                                        <MarkerF
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            key={marker.id}
                                            icon={{
                                                url: marker.driverActiveStatus ? carIconOnline : carIconOffline,
                                                scaledSize: (window.google && window.google.maps && window.google.maps.Size) ? new window.google.maps.Size(42, 42) : undefined,
                                            }}
                                            opacity={marker.driverActiveStatus ? 1 : 0.75}
                                            onClick={() => handleMarkerClick(marker.id)}
                                        >
                                            {marker.show && (
                                                <InfoWindow
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    options={{ pixelOffset: (window.google && window.google.maps && window.google.maps.Size) ? new window.google.maps.Size(0, -32) : undefined }}
                                                    onCloseClick={() => handleClose(marker.id)}
                                                >
                                                    <Box sx={{
                                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                        minWidth: 230,
                                                        maxWidth: 290,
                                                        p: 0.5,
                                                        color: '#1e293b'
                                                    }}>
                                                        {/* Header: Driver Name & Status Badge */}
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            pb: 1,
                                                            mb: 1,
                                                            borderBottom: '1px solid #e2e8f0'
                                                        }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                                                                {marker.drivername}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={marker.driverActiveStatus ? (isPt ? 'Em Rota' : 'On Route') : (isPt ? 'Offline' : 'Offline')}
                                                                    sx={{
                                                                        height: 20,
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        bgcolor: marker.driverActiveStatus ? '#dcfce7' : '#f1f5f9',
                                                                        color: marker.driverActiveStatus ? '#15803d' : '#64748b'
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleClose(marker.id);
                                                                    }}
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        color: '#94a3b8',
                                                                        fontSize: 12,
                                                                        fontWeight: 'bold',
                                                                        padding: '2px'
                                                                    }}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </Box>
                                                        </Box>

                                                        {/* Placa & Veículo */}
                                                        <Box sx={{ mb: 1 }}>
                                                            {marker.plate && (
                                                                <Box sx={{
                                                                    display: 'inline-block',
                                                                    bgcolor: '#0f172a',
                                                                    color: '#f8fafc',
                                                                    fontSize: 11,
                                                                    fontWeight: 800,
                                                                    px: 1,
                                                                    py: 0.2,
                                                                    borderRadius: '5px',
                                                                    letterSpacing: '1px',
                                                                    fontFamily: 'monospace',
                                                                    mb: 0.5
                                                                }}>
                                                                    {marker.plate.toUpperCase()}
                                                                </Box>
                                                            )}
                                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                                                                {marker.vehicleDisplay}
                                                            </Typography>
                                                        </Box>

                                                        {/* Última Telemetria */}
                                                        <Box sx={{ pt: 0.5, borderTop: '1px dashed #f1f5f9' }}>
                                                            <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
                                                                {marker.driverActiveStatus ? (isPt ? 'Última telemetria:' : 'Last ping:') : (isPt ? 'Último contato:' : 'Last online:')}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#475569' }}>
                                                                {formatLastSeen(marker.lastTimeSeen, marker.driverActiveStatus)}
                                                            </Typography>
                                                        </Box>

                                                        {/* Rota Ativa */}
                                                        {marker.bookingRef && (
                                                            <Box sx={{
                                                                mt: 1,
                                                                p: 0.6,
                                                                bgcolor: '#f0f9ff',
                                                                borderRadius: '6px',
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color: '#0284c7'
                                                            }}>
                                                                OS: #{marker.bookingRef}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </InfoWindow>
                                            )}
                                        </MarkerF>
                                    ))}
                                </GoogleMap>
                            ) : (
                                <Box sx={{ height: '580px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                                    <CircularLoading />
                                </Box>
                            )}
                        </Card>
                    </Grid>

                    {/* Sidebar Lateral: Controle da Frota em Campo */}
                    <Grid item xs={12} lg={3.5}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Widget 1: Taxa de Ocupação da Frota */}
                            <Card sx={{
                                p: 2.5,
                                borderRadius: '20px',
                                border: '1px solid #e2e8f0',
                                bgcolor: '#ffffff',
                                boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)'
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                                    {isPt ? "Eficiência da Frota" : "Fleet Utilization"}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                                        {isPt ? "Em operação" : "Operating"}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#15803d' }}>
                                        {fleetOccupancy}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={fleetOccupancy}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#e2e8f0',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            bgcolor: '#10b981'
                                        }
                                    }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                                        🟢 {activeCount} {isPt ? "em rota" : "active"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                        ⚪ {Math.max(driverCount - activeCount, 0)} {isPt ? "disponíveis" : "idle"}
                                    </Typography>
                                </Box>
                            </Card>

                            {/* Widget 2: Lista / Feed Interativo de Veículos e Motoristas */}
                            <Card sx={{
                                p: 2.5,
                                borderRadius: '20px',
                                border: '1px solid #e2e8f0',
                                bgcolor: '#ffffff',
                                boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        {isPt ? "Veículos Monitorados" : "Tracked Vehicles"}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`${filteredLocations.length}`}
                                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 11, height: 20 }}
                                    />
                                </Box>

                                {/* Campo de busca rápida */}
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={isPt ? "Buscar placa ou motorista..." : "Search plate or driver..."}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            bgcolor: '#f8fafc',
                                            fontSize: 13
                                        }
                                    }}
                                />

                                {/* Lista de Veículos com Scroll */}
                                <Box sx={{
                                    maxHeight: 380,
                                    overflowY: 'auto',
                                    pr: 0.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1
                                }}>
                                    {filteredLocations.length > 0 ? (
                                        filteredLocations.map((item) => {
                                            const isSelected = selectedMarkerId === item.id;
                                            return (
                                                <Box
                                                    key={item.id}
                                                    onClick={() => focusVehicle(item)}
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '12px',
                                                        border: isSelected ? '1.5px solid #2563eb' : '1px solid #f1f5f9',
                                                        bgcolor: isSelected ? '#eff6ff' : '#ffffff',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            bgcolor: isSelected ? '#eff6ff' : '#f8fafc',
                                                            borderColor: isSelected ? '#2563eb' : '#cbd5e1'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                                                            {item.drivername}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box sx={{
                                                                width: 7,
                                                                height: 7,
                                                                borderRadius: '50%',
                                                                bgcolor: item.driverActiveStatus ? '#16a34a' : '#94a3b8'
                                                            }} />
                                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: item.driverActiveStatus ? '#16a34a' : '#94a3b8' }}>
                                                                {item.driverActiveStatus ? (isPt ? "Em rota" : "Active") : (isPt ? "Offline" : "Offline")}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>
                                                            {item.vehicleDisplay}
                                                        </Typography>
                                                        {item.plate && (
                                                            <Typography sx={{
                                                                fontSize: 10,
                                                                fontWeight: 800,
                                                                bgcolor: '#0f172a',
                                                                color: '#f8fafc',
                                                                px: 0.8,
                                                                py: 0.1,
                                                                borderRadius: '4px',
                                                                fontFamily: 'monospace'
                                                            }}>
                                                                {item.plate.toUpperCase()}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })
                                    ) : (
                                        <Typography variant="body2" sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>
                                            {isPt ? "Nenhum veículo encontrado" : "No vehicles found"}
                                        </Typography>
                                    )}
                                </Box>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>

                {/* 4. Gráfico Inferior: Desempenho Operacional */}
                <Card sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: '20px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#ffffff',
                    boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)'
                }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                {isPt ? "Desempenho e Volume de Rotas" : "Operational Routes Performance"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                                {isPt ? "Histórico de ordens concluídas vs canceladas por mês" : "Completed vs cancelled work orders history"}
                            </Typography>
                        </Box>
                    </Box>

                    {Chart ? (
                        <Chart
                            options={{
                                ...barChartOptions,
                                chart: {
                                    ...barChartOptions.options?.chart,
                                    toolbar: { show: false }
                                },
                                colors: ['#2563eb', '#f43f5e']
                            }}
                            series={barChart}
                            type="area"
                            width="100%"
                            height={340}
                        />
                    ) : null}
                </Card>
            </Box>
    );
};

export default Dashboard;